import React from 'react';
import { motion } from 'framer-motion';
import { Flame } from 'lucide-react';
import { wrappedTranslations } from '../../../translations/wrapped';

interface FirstSetPowerCardProps {
    winRateWhenWon: number;
    firstSetWins: number;
    totalMatches: number;
    lang: 'en' | 'fr' | 'de' | 'it';
}

const FirstSetPowerCard: React.FC<FirstSetPowerCardProps> = ({
    winRateWhenWon,
    firstSetWins,
    totalMatches,
    lang,
}) => {
    const t = wrappedTranslations[lang];
    // Safely handle the win rate, capping at 100% and handling NaN
    const safeWinRate = isNaN(winRateWhenWon) ? 0 : Math.min(100, winRateWhenWon);

    return (
        <div className="h-full w-full flex flex-col items-center justify-center text-center p-6">
            <div className="max-w-4xl w-full flex flex-col items-center">
                <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring" }}
                >
                    <Flame className="w-20 h-20 md:w-32 md:h-32 mb-8 mx-auto text-[#1C7AD6]" />
                </motion.div>

                <motion.h2
                    className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 text-gray-900 tracking-tight whitespace-nowrap px-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                >
                    {t.firstSetPower}
                </motion.h2>

                <motion.p
                    className="text-base md:text-lg text-gray-600 mb-12 font-light px-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                >
                    {t.startingStrong}
                </motion.p>

                <motion.div
                    className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8 shadow-lg w-full max-w-2xl overflow-hidden"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                >
                    <div className="mb-8 md:mb-10">
                        <div className="text-5xl md:text-7xl font-bold text-[#1C7AD6] mb-2 break-words">
                            {safeWinRate.toFixed(0)}%
                        </div>
                        <div className="text-base md:text-lg text-gray-700 font-medium px-2">{t.winRateWhenWinSet1}</div>
                    </div>

                    <div className="grid grid-cols-2 gap-6 md:gap-8">
                        <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                            <div className="text-5xl md:text-7xl font-bold text-gray-900 mb-1">{firstSetWins}</div>
                            <div className="text-xs md:text-sm text-gray-600 uppercase tracking-wider">{t.firstSetsWon}</div>
                        </div>
                        <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                            <div className="text-5xl md:text-7xl font-bold text-gray-900 mb-1">{totalMatches}</div>
                            <div className="text-xs md:text-sm text-gray-600 uppercase tracking-wider">{t.totalMatches}</div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default FirstSetPowerCard;
